import {
  useEntityRemindersSuspense,
  type EntityRemindersProps,
  type EntityRemindersResponse
} from "@swivu/client-api";
import CommonStyles from "./CommonStyles.js";
import { ArrowPathIcon, ClockIcon } from "@heroicons/react/24/outline";
import {
  Button,
  cn,
  groupBy,
  spreadReminders,
  useIsGummy
} from "@swivu/shared-ui";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

type Props = EntityRemindersProps & { title: string; timezone: string };

const EntityReminders = (variables: Props) => {
  const { data: reminders } = useEntityRemindersSuspense(variables.entity_type)(
    { variables }
  );
  if (!reminders || reminders?.length === 0) return null;
  return (
    <CommonStyles
      title={`Reminders`}
      tone="reminders"
      cardTitle={`${variables.title} Reminders`}
      cardSubtitle={`Reminders`}
      Icon={ClockIcon}
      badge={reminders?.length}
      contentClassNames="max-w-xl! mx-auto"
    >
      <Reminders reminders={reminders} timezone={variables.timezone} />
    </CommonStyles>
  );
};

const selectDates = (reminders: EntityRemindersResponse, timezone: string) => {
  const spreadedReminders = spreadReminders(reminders ?? [], timezone);
  return (
    spreadedReminders.map((d) => {
      const alertDate = DateTime.fromISO(d.alert_date ?? "", {
        zone: timezone
      });
      return {
        ...d,
        start_time: d.alert_date
          ? alertDate.startOf("day").toISO()!
          : new Date().toISOString(),
        end_time: d.alert_date
          ? alertDate.endOf("day").toISO()!
          : new Date().toISOString(),
        color: "mono-100",
        title: d.description ?? "",
        viewTransitionProps: {}
      };
    }) ?? []
  );
};

type SelectedDate = ReturnType<typeof selectDates>[number] & {
  description: string;
};

const intervalLabel = (interval: SelectedDate["recurring_interval"]) => {
  switch (interval) {
    case "Daily":
      return "Every day";
    case "Weekly":
      return "Every week";
    case "Monthly":
      return "Every month";
    case "Yearly":
      return "Every year";
    default:
      return "One-off";
  }
};

const generateReminderIntervalMessage = (value: SelectedDate[]) => {
  if (value[0]?.recurring_interval === "Never") return `Only reminder`;
  return `${value.length} ${value[0]?.recurring_interval?.toLocaleLowerCase() ?? ""} reminders`;
};

const Reminders = ({
  reminders,
  timezone
}: {
  reminders: EntityRemindersResponse;
  timezone: string;
}) => {
  const isGummy = useIsGummy();
  const selectedDates = selectDates(reminders, timezone);

  const nonNullDescription = selectedDates.filter(
    (d) => d.description !== null
  ) as SelectedDate[];
  const groupedByDate = groupBy(nonNullDescription, "description");

  return (
    <div className="grid w-full gap-y-4 pt-2">
      {Object.entries(groupedByDate).map(([key, value]) =>
        isGummy ? (
          <ReminderGroupGummy
            key={key}
            description={key}
            dates={value}
            timezone={timezone}
          />
        ) : (
          <ReminderGroupLegacy
            key={key}
            description={key}
            dates={value}
            timezone={timezone}
          />
        )
      )}
    </div>
  );
};

const ReminderGroupLegacy = ({
  description,
  dates,
  timezone
}: {
  description: string;
  dates: SelectedDate[];
  timezone: string;
}) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? dates : dates.slice(0, 6);
  return (
    <div className="dark:border-mono-700/50 dark:bg-mono-950 flex w-full min-w-0 flex-col gap-4 rounded-md border bg-white p-5 md:flex-row md:justify-between md:gap-x-16">
      <div className="dark:border-mono-700/50 min-w-0 border-b pb-3 md:flex-1 md:border-none md:pb-0">
        <div className="dark:text-mono-200 sticky top-0 text-lg leading-5">
          {description}
        </div>
        <div className="dark:text-mono-200 text-sm italic md:mt-1">
          {generateReminderIntervalMessage(dates)}
        </div>
      </div>
      <div className="flex-none">
        {visible.map((d) => (
          <div
            key={`${d.title}-${d.alert_date ?? d.start_time}`}
            className="flex flex-col"
          >
            <div className="text-mono-700 dark:text-mono-300 font-mono tabular-nums">
              {DateTime.fromISO(d.alert_date ?? d.start_time, {
                zone: timezone
              }).toFormat("MMM dd yyyy',' h:mm a")}
            </div>
          </div>
        ))}
        {!showAll && dates.length > 6 && (
          <Button
            className="mobile-pref-inline-button mt-4 block md:ml-auto"
            action="secondary"
            size="small"
            onClick={() => setShowAll(true)}
          >
            Show all reminders
          </Button>
        )}
      </div>
    </div>
  );
};

const PREVIEW_COUNT = 11;

const ReminderGroupGummy = ({
  description,
  dates,
  timezone
}: {
  description: string;
  dates: SelectedDate[];
  timezone: string;
}) => {
  const [showAll, setShowAll] = useState(false);

  const sorted = useMemo(
    () =>
      [...dates].sort((a, b) =>
        (a.alert_date ?? a.start_time).localeCompare(
          b.alert_date ?? b.start_time
        )
      ),
    [dates]
  );

  const now = DateTime.now().setZone(timezone);
  const upcoming = sorted.filter(
    (d) =>
      DateTime.fromISO(d.alert_date ?? d.start_time, { zone: timezone }) >=
      now.startOf("day")
  );
  const isAllPast = upcoming.length === 0;
  const featured = upcoming[0] ?? sorted[sorted.length - 1];
  const tail = isAllPast ? [] : upcoming.slice(1);

  const visible = showAll ? tail : tail.slice(0, PREVIEW_COUNT);
  const hidden = tail.length - visible.length;

  const interval = sorted[0]?.recurring_interval ?? "Never";
  const isRecurring = interval !== "Never";

  const timeKeys = new Set(
    sorted.map((d) =>
      DateTime.fromISO(d.alert_date ?? d.start_time, {
        zone: timezone
      }).toFormat("HH:mm")
    )
  );
  const sharedTime =
    timeKeys.size === 1 && featured
      ? DateTime.fromISO(featured.alert_date ?? featured.start_time, {
          zone: timezone
        }).toFormat("h:mm a")
      : null;

  return (
    <article
      className={cn(
        "flex w-full flex-col gap-5 p-5 md:p-6",
        "shadow-card rounded-surface-lg bg-white dark:bg-mono-900"
      )}
    >
      <header className="flex flex-col gap-3">
        <h3 className="text-2xl font-black leading-tight tracking-tight text-mono-900 dark:text-white md:text-3xl break-words">
          {description}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          {isRecurring && (
            <SummaryPill tone="alert">
              <ArrowPathIcon className="size-4" strokeWidth={3} aria-hidden />
              {intervalLabel(interval)}
            </SummaryPill>
          )}
          {sharedTime && (
            <SummaryPill tone="neutral">
              <ClockIcon className="size-4" strokeWidth={3} aria-hidden />
              {sharedTime}
            </SummaryPill>
          )}
          {dates.length > 1 && (
            <SummaryPill tone="neutral">{dates.length} total</SummaryPill>
          )}
        </div>
      </header>

      {featured && (
        <FeaturedReminder
          date={featured}
          timezone={timezone}
          label={isAllPast ? "Last reminder" : "Next reminder"}
          showTime={!sharedTime}
        />
      )}

      {tail.length > 0 && (
        <section className="flex flex-col gap-3">
          <div className="text-xs font-black uppercase tracking-widest text-mono-600 dark:text-mono-400">
            Upcoming
          </div>
          <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
            {visible.map((d, idx) => (
              <UpcomingChip
                key={`${d.title}-${d.alert_date ?? d.start_time}-${idx}`}
                date={d}
                timezone={timezone}
                showTime={!sharedTime}
              />
            ))}
          </ul>
          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className={cn(
                "mobile-pref-inline-button inline-flex h-14 items-center justify-center self-start rounded-full px-6 text-sm font-black uppercase tracking-widest",
                "shadow-raised",
                "bg-white dark:bg-mono-800 text-mono-900 dark:text-white",
                "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
                "motion-reduce:transform-none motion-reduce:transition-none",
                "outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40"
              )}
            >
              Show {hidden} more
            </button>
          )}
        </section>
      )}
    </article>
  );
};

const SummaryPill = ({
  tone,
  children
}: {
  tone: "alert" | "neutral";
  children: React.ReactNode;
}) => (
  <span
    className={cn(
      "inline-flex h-10 items-center gap-2 rounded-full px-4 text-xs font-black uppercase tracking-widest",
      "shadow-raised-soft",
      tone === "alert"
        ? "bg-tile-alert text-white"
        : "bg-white dark:bg-mono-800 text-mono-900 dark:text-white"
    )}
    style={
      tone === "alert"
        ? { textShadow: "var(--text-shadow-on-color)" }
        : undefined
    }
  >
    {children}
  </span>
);

const FeaturedReminder = ({
  date,
  timezone,
  label,
  showTime
}: {
  date: SelectedDate;
  timezone: string;
  label: string;
  showTime: boolean;
}) => {
  const dt = DateTime.fromISO(date.alert_date ?? date.start_time, {
    zone: timezone
  });
  return (
    <section className="flex flex-col gap-3">
      <div className="text-xs font-black uppercase tracking-widest text-mono-600 dark:text-mono-400">
        {label}
      </div>
      <div
        className={cn(
          "relative flex flex-col gap-1 overflow-hidden rounded-surface-md p-5 md:p-6",
          "shadow-raised bg-tile-alert text-white"
        )}
        style={{ textShadow: "var(--text-shadow-on-color-strong)" }}
      >
        <span aria-hidden className="top-gloss" />
        <span className="relative text-sm font-black uppercase tracking-widest opacity-95">
          {dt.toFormat("cccc")}
        </span>
        <span className="relative text-3xl font-black leading-tight md:text-4xl">
          {dt.toFormat("LLLL d, yyyy")}
        </span>
        {showTime && (
          <span className="relative mt-1 text-xl font-black tabular-nums md:text-2xl">
            {dt.toFormat("h:mm a")}
          </span>
        )}
      </div>
    </section>
  );
};

const UpcomingChip = ({
  date,
  timezone,
  showTime
}: {
  date: SelectedDate;
  timezone: string;
  showTime: boolean;
}) => {
  const dt = DateTime.fromISO(date.alert_date ?? date.start_time, {
    zone: timezone
  });
  return (
    <li
      className={cn(
        "flex flex-col items-center justify-center gap-0.5 rounded-surface-sm px-3 py-3 text-center tabular-nums leading-tight",
        "shadow-raised-soft bg-white dark:bg-mono-800 text-mono-900 dark:text-white"
      )}
    >
      <span className="text-xs font-black uppercase tracking-widest text-mono-500 dark:text-mono-400">
        {dt.toFormat("ccc")}
      </span>
      <span className="text-base font-black leading-tight">
        {dt.toFormat("LLL d")}
      </span>
      {showTime && (
        <span className="text-[11px] font-black uppercase tracking-wider text-mono-500 dark:text-mono-400">
          {dt.toFormat("h:mm a")}
        </span>
      )}
    </li>
  );
};

export default Reminders;

export { EntityReminders };
